import React, { useState } from "react";
import { track } from "/src/helpers/analytics";
import { useOutletContext } from "react-router";

const DocumentationPage = () => {
    const { model } = useOutletContext();
    const correspondence = model?.correspondence || [];
    const [expandedId, setExpandedId] = useState(null);
    const isTradeByPax = model?.authenticationLevel === "TradeBookingByPax";

    const toggle = (id) => setExpandedId(expandedId === id ? null : id);

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    };

    if (isTradeByPax) {
        return (
            <div>
                <h1 className="text-3xl font-poppins font-semibold text-primary mb-6">Documents</h1>
                <div className="bg-white rounded-card p-8 text-center">
                    <div className="text-error mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v.01M12 12V8m9 4a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <p className="text-primary font-semibold text-lg mb-1">Sign in as a travel agent to view booking documents.</p>
                    <p className="text-content_2">Not an agent? Please reach out to the agency that arranged your booking.</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h1 className="text-3xl font-poppins font-semibold text-primary mb-6">Documents</h1>

            {correspondence.length === 0 && (
                <div className="bg-white rounded-card p-8 text-center">
                    <div className="text-content_3 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V6a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V18a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <p className="text-content_2">Your booking documents will appear here when available.</p>
                </div>
            )}

            <div className="space-y-4">
                {correspondence.map((item) => (
                    <div key={item.correspondenceID} className="bg-white rounded-card overflow-hidden">
                        {/* Header */}
                        <button
                            onClick={() => toggle(item.correspondenceID)}
                            className="w-full flex items-center gap-4 p-5 text-left cursor-pointer hover:bg-content_5/50 transition"
                        >
                            <div className="text-emp shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-base font-semibold text-primary font-poppins truncate">{item.subject}</h2>
                                <div className="flex items-center gap-2 mt-0.5">
                                    {item.date && (
                                        <span className="text-xs text-content_3">{formatDate(item.date)}</span>
                                    )}
                                    {item.date && item.attachments?.length > 0 && (
                                        <span className="text-xs text-content_4">·</span>
                                    )}
                                    {item.attachments?.length > 0 && (
                                        <span className="text-xs text-content_3">
                                            {item.attachments.length} attachment{item.attachments.length !== 1 ? "s" : ""}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="text-content_3 shrink-0 transition-transform" style={{ transform: expandedId === item.correspondenceID ? "rotate(180deg)" : "rotate(0)" }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>

                        {/* Expanded content */}
                        {expandedId === item.correspondenceID && (
                            <div className="border-t border-content_5">
                                {/* Attachments */}
                                {item.attachments?.length > 0 && (
                                    <div className="px-5 py-4 bg-content_5/30">
                                        <p className="text-xs font-semibold text-content_2 uppercase tracking-wide mb-3">Attachments</p>
                                        <div className="flex flex-wrap gap-3">
                                            {item.attachments.map((att) => (
                                                <a
                                                    key={att.attachmentID}
                                                    href={`/api/correspondence/attachment/${att.attachmentID}`}
                                                    onClick={() => track("document_download", { attachment_id: att.attachmentID, file_name: att.fileName || att.name || undefined })}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border border-content_4 hover:border-emp hover:text-emp text-sm text-primary transition group"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-content_3 group-hover:text-emp transition" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                                    </svg>
                                                    <span className="truncate max-w-xs">{att.displayName}</span>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Email body */}
                                {item.body && (
                                    <div className="p-5">
                                        <p className="text-xs font-semibold text-content_2 uppercase tracking-wide mb-3">Message</p>
                                        <iframe
                                            srcDoc={item.body}
                                            title={item.subject || "Email content"}
                                            className="w-full border-0 rounded-lg bg-white"
                                            style={{ minHeight: "300px", maxHeight: "600px" }}
                                            sandbox="allow-same-origin"
                                            onLoad={(e) => {
                                                const doc = e.target.contentDocument;
                                                if (doc) {
                                                    e.target.style.height = doc.documentElement.scrollHeight + "px";
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DocumentationPage;
